import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, CircularProgress, Alert, IconButton, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import HubMicroservicePanel from './HubMicroservicePanel';
import HubStatusBadge from './HubStatusBadge';
import {
  getInstanceMonitorCbvRaw,
  getInstanceMonitorMsvRaw,
  getInstanceMonitorAzureRaw,
} from '../../services/apiService';

// Tab styles matching instance-monitor exactly
const TAB_ACTIVE   = { color: '#2563eb', borderColor: '#2563eb' };
const TAB_INACTIVE = { color: '#64748b', borderColor: 'transparent' };

// Shared input base style
const INPUT_SX = {
  width: '100%', border: '1px solid #e2e8f0', borderRadius: '6px',
  bgcolor: '#f8fafc', px: '10px', py: '7px',
  fontSize: '0.8rem', color: '#0f172a', outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
  '&:focus': { borderColor: '#2563eb', bgcolor: '#ffffff' },
};

// ─── Info Tab ─────────────────────────────────────────────────────────────────

// Format raw upTime string (e.g. "2026.03.12 14:21:38 (8764461 seconds)") to "MMM D, HH:mm".
const formatUpTime = (raw, cbvData = {}) => {
  if (!raw) return null;
  const fmt = (d) => d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  const m = raw.match(/(\d{4})[.\-/](\d{2})[.\-/](\d{2})\s+(\d{2}):(\d{2})/);
  if (m) return fmt(new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
  if (cbvData.upTimeSeconds && cbvData.lastFetched) {
    const start = new Date(new Date(cbvData.lastFetched).getTime() - cbvData.upTimeSeconds * 1000);
    return fmt(start);
  }
  const t = raw.match(/(\d{1,2}):(\d{2})/);
  return t ? `${t[1].padStart(2,'0')}:${t[2]}` : raw;
};

const InfoTab = ({ instance, onSaveMeta }) => {
  const [label,   setLabel]   = useState(instance.label  || '');
  const [team,    setTeam]    = useState(instance.team   || '');
  const [notes,   setNotes]   = useState(instance.notes  || '');
  const [saving,  setSaving]  = useState(false);
  const [saveOk,  setSaveOk]  = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const cbvData   = instance.cbvData || {};
  const status    = instance.status === 'active' ? 'green'
                  : instance.status === 'error'  ? 'red' : 'N/A';

  const handleSave = async () => {
    setSaving(true); setSaveOk(false); setSaveErr('');
    try {
      await onSaveMeta({ label, team, notes });
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 3000);
    } catch (err) {
      setSaveErr(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sectionTitle = {
    fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.1em', color: '#64748b',
    display: 'flex', alignItems: 'center', gap: '6px', mb: '12px',
  };
  const rowSx = { display: 'flex', alignItems: 'center', gap: '10px' };
  const labelSx = { fontSize: '0.78rem', color: '#64748b', width: 100, flexShrink: 0 };
  const monoChip = {
    fontFamily: 'monospace', fontSize: '0.72rem',
    bgcolor: '#f1f5f9', color: '#475569',
    px: '6px', py: '1px', borderRadius: '3px',
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '28px' }}>

      {/* ── Left: Live Server Info ──────────────────── */}
      <Box>
        <Box sx={sectionTitle}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3b82f6', flexShrink: 0 }} />
          Live Server Info
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: '20px' }}>
          <Box sx={rowSx}>
            <Box sx={labelSx}>Status</Box>
            <HubStatusBadge status={status} variant="badge" />
          </Box>
          {cbvData.version && (
            <Box sx={rowSx}>
              <Box sx={labelSx}>Version</Box>
              <Box component="span" sx={monoChip}>{cbvData.version}</Box>
            </Box>
          )}
          {cbvData.branch && (
            <Box sx={rowSx}>
              <Box sx={labelSx}>Branch</Box>
              <Box component="span" sx={monoChip}>{cbvData.branch}</Box>
            </Box>
          )}
          {cbvData.upTime && (
            <Box sx={rowSx}>
              <Box sx={labelSx}>Up Time</Box>
              <Box component="span" sx={monoChip}>{formatUpTime(cbvData.upTime, cbvData)}</Box>
            </Box>
          )}
        </Box>

      </Box>

      {/* ── Right: Tags & Notes ─────────────────────── */}
      <Box>
        <Box sx={sectionTitle}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#8b5cf6', flexShrink: 0 }} />
          Instance Tags &amp; Notes
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Box>
              <Box component="label" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#475569', mb: '4px' }}>
                Label
              </Box>
              <Box component="input" type="text" value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="dev, staging…"
                sx={INPUT_SX} />
            </Box>
            <Box>
              <Box component="label" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#475569', mb: '4px' }}>
                Team
              </Box>
              <Box component="input" type="text" value={team}
                onChange={(e) => setTeam(e.target.value)}
                placeholder="AI team…"
                sx={INPUT_SX} />
            </Box>
          </Box>
          <Box>
            <Box component="label" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#475569', mb: '4px' }}>
              Notes
            </Box>
            <Box component="textarea" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this instance…"
              rows={3}
              sx={{ ...INPUT_SX, resize: 'none', display: 'block' }} />
          </Box>
          {saveErr && <Box sx={{ fontSize: '0.75rem', color: '#dc2626' }}>{saveErr}</Box>}
          {saveOk  && <Box sx={{ fontSize: '0.75rem', color: '#16a34a' }}>Saved successfully.</Box>}
          <Button size="small" variant="contained"
            startIcon={saving ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 15 }} />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              fontSize: '0.78rem', textTransform: 'none', fontWeight: 600,
              bgcolor: '#2563eb', alignSelf: 'flex-start', px: '16px',
              '&:hover': { bgcolor: '#1d4ed8' },
            }}>
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Raw Text Tab ─────────────────────────────────────────────────────────────

const RawTextTab = ({ instanceName, baseURL, type }) => {
  const [rawText, setRawText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [copied,  setCopied]  = useState(false);
  const [fetched, setFetched] = useState(false);

  const label    = type === 'cbv' ? 'getCBV.action' : type === 'msv' ? 'getMSV.action' : 'checkAzure.action';
  const fileName = `${type === 'cbv' ? 'getCBV' : type === 'msv' ? 'getMSV' : 'checkAzure'}_${instanceName}.txt`;

  const doFetch = useCallback(async (force = false) => {
    if (!instanceName) return;
    setLoading(true); setError(null);
    try {
      const text = type === 'cbv'
        ? await getInstanceMonitorCbvRaw(instanceName, baseURL, force)
        : type === 'msv'
        ? await getInstanceMonitorMsvRaw(instanceName, baseURL, force)
        : await getInstanceMonitorAzureRaw(instanceName, baseURL, force);
      setRawText(typeof text === 'string' ? text : JSON.stringify(text, null, 2));
      setFetched(true);
    } catch (err) {
      setError(err.message || `Failed to fetch ${label}`);
    } finally {
      setLoading(false);
    }
  }, [instanceName, baseURL, type, label]);

  // Initial load: serve from cache (force=false)
  useEffect(() => { if (!fetched) doFetch(false); }, [fetched, doFetch]);

  const handleCopy = async () => {
    if (!rawText) return;
    try { await navigator.clipboard.writeText(rawText); }
    catch {
      const el = document.createElement('textarea');
      el.value = rawText; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!rawText) return;
    const url = URL.createObjectURL(new Blob([rawText], { type: 'text/plain;charset=utf-8' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: fileName });
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px', gap: 1 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: '#64748b' }}>
          {label} Response
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" title="Refresh" onClick={() => doFetch(true)} disabled={loading}
          sx={{ color: '#64748b', '&:hover': { color: '#1e293b' } }}>
          <RefreshIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" title={copied ? 'Copied!' : 'Copy'} onClick={handleCopy}
          disabled={!rawText || loading}
          sx={{ color: copied ? '#16a34a' : '#64748b', '&:hover': { color: '#1e293b' } }}>
          {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
        </IconButton>
        <IconButton size="small" title="Download" onClick={handleDownload}
          disabled={!rawText || loading}
          sx={{ color: '#64748b', '&:hover': { color: '#1e293b' } }}>
          <DownloadIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Content area: overlay spinner on stale content during refresh */}
      <Box sx={{ position: 'relative' }}>
        {/* Show previous content greyed while reloading, or spinner for initial load */}
        {loading && !rawText && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && !loading && (
          <Alert severity="error" action={<Button size="small" onClick={() => doFetch(true)}>Retry</Button>}
            sx={{ fontSize: '0.8rem' }}>{error}</Alert>
        )}
        {rawText && (
          <Box component="pre" sx={{
            bgcolor: '#0f172a', borderRadius: '8px', p: '14px 16px',
            maxHeight: 288, overflow: 'auto',
            fontFamily: '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
            fontSize: '0.72rem', lineHeight: 1.6,
            color: '#86efac', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            opacity: loading ? 0.4 : 1,
            transition: 'opacity 0.15s',
          }}>
            {rawText}
          </Box>
        )}
        {!loading && !error && !rawText && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>No data available</Typography>
          </Box>
        )}
        {/* Centered spinner overlay when refreshing existing content */}
        {loading && rawText && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '8px',
          }}>
            <CircularProgress size={24} sx={{ color: '#86efac' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── InstanceExpandedTabs ─────────────────────────────────────────────────────

/**
 * @param {{ instance: object, hubConfigured: boolean, onSaveMeta: function }} props
 */
const InstanceExpandedTabs = ({ instance, hubConfigured, onSaveMeta }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!instance) return null;

  // Ensure base URL always includes the context path segment.
  // Some testinstances store baseURL as https://host.com (no context), others as https://host.com/ctx.
  const rawBase = (instance.baseURL || '').replace(/\/$/, '');
  const ctxName = instance.contextName || '';
  const fullBaseURL = ctxName && !rawBase.endsWith('/' + ctxName)
    ? `${rawBase}/${ctxName}`
    : rawBase;

  const instanceWithFullBase = { ...instance, baseURL: fullBaseURL };

  const tabs = [
    {
      label: 'Info',
      content: (
        <InfoTab
          instance={instanceWithFullBase}
          onSaveMeta={onSaveMeta || (() => Promise.resolve())}
        />
      ),
    },
    {
      label: 'getCBV',
      content: <RawTextTab key={`cbv-${instance.contextName}`} instanceName={instance.contextName} baseURL={fullBaseURL} type="cbv" />,
    },
    {
      label: 'getMSV',
      content: <RawTextTab key={`msv-${instance.contextName}`} instanceName={instance.contextName} baseURL={fullBaseURL} type="msv" />,
    },
    {
      label: 'Azure',
      content: <RawTextTab key={`azure-${instance.contextName}`} instanceName={instance.contextName} baseURL={fullBaseURL} type="azure" />,
    },
    ...(hubConfigured ? [{
      label: 'MS from Hub',
      content: (
        <HubMicroservicePanel
          instanceName={instance.contextName}
          baseURL={fullBaseURL}
          team={instance.team}
          hubConfigured={hubConfigured}
        />
      ),
    }] : []),
  ];

  return (
    /* border-t border-slate-200 bg-slate-50 */
    <Box sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>

      {/* Tab bar */}
      <Box sx={{
        display: 'flex', borderBottom: '1px solid #e2e8f0',
        bgcolor: '#ffffff', px: '24px',
      }}>
        {tabs.map((tab, i) => {
          const isActive = activeTab === i;
          const style = isActive ? TAB_ACTIVE : TAB_INACTIVE;
          return (
            <Box
              key={tab.label}
              component="button"
              onClick={() => setActiveTab(i)}
              sx={{
                px: '16px', py: '10px',
                fontSize: '0.82rem', fontWeight: 500,
                border: 'none', background: 'none', cursor: 'pointer',
                color: style.color,
                borderBottom: `2px solid ${style.borderColor}`,
                mb: '-1px',
                transition: 'color 0.15s, border-color 0.15s',
                '&:hover': !isActive ? { color: '#334155', borderColor: '#cbd5e1' } : {},
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>

      {/* Tab content */}
      <Box sx={{ p: '20px 24px 24px' }}>
        {tabs[activeTab]?.content}
      </Box>
    </Box>
  );
};

export default InstanceExpandedTabs;
